import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Snackbar,
  Divider,
  List,
  ListItem,
  ListItemText,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Receipt as ReceiptIcon,
  CheckCircle as ApproveIcon,
  Cancel as CancelIcon,
  LocalShipping as ShippingIcon,
  Assignment as AssignmentIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Print as PrintIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const PurchaseOrders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [procurementItems, setProcurementItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [dialogMode, setDialogMode] = useState('create'); // create, edit, view
  const [filters, setFilters] = useState({
    status: '',
    vendor: '',
    search: '',
    dateFrom: null,
    dateTo: null
  });
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    totalValue: 0,
    averageOrderValue: 0
  });

  const [formData, setFormData] = useState({
    poNumber: '',
    vendor: '',
    project: '',
    requestedBy: '',
    items: [],
    deliveryAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    expectedDeliveryDate: null,
    paymentTerms: '',
    notes: '',
    urgency: 'normal'
  });

  const [newItem, setNewItem] = useState({
    item: '',
    description: '',
    quantity: 1,
    unitPrice: 0,
    unit: 'pcs'
  });

  const statusOptions = [
    { value: 'draft', label: 'Draft', color: 'default' },
    { value: 'pending_approval', label: 'Pending Approval', color: 'warning' },
    { value: 'approved', label: 'Approved', color: 'info' },
    { value: 'sent_to_vendor', label: 'Sent to Vendor', color: 'primary' },
    { value: 'confirmed', label: 'Confirmed', color: 'success' },
    { value: 'partially_received', label: 'Partially Received', color: 'warning' },
    { value: 'completed', label: 'Completed', color: 'success' },
    { value: 'cancelled', label: 'Cancelled', color: 'error' }
  ];

  const urgencyOptions = [
    { value: 'low', label: 'Low', color: 'success' },
    { value: 'normal', label: 'Normal', color: 'info' },
    { value: 'high', label: 'High', color: 'warning' },
    { value: 'urgent', label: 'Urgent', color: 'error' }
  ];

  const units = ['pcs', 'kg', 'lbs', 'ft', 'm', 'sqft', 'sqm', 'gal', 'L', 'box', 'roll', 'sheet'];

  useEffect(() => {
    fetchPurchaseOrders();
    fetchVendors();
    fetchProcurementItems();
    fetchStats();
  }, [filters]);

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.vendor) params.append('vendor', filters.vendor);
      if (filters.search) params.append('search', filters.search);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom.toISOString());
      if (filters.dateTo) params.append('dateTo', filters.dateTo.toISOString());
      
      const response = await api.get(`/purchase-orders?${params.toString()}`);
      setPurchaseOrders(response.data);
    } catch (error) {
      setError('Failed to fetch purchase orders');
      console.error('Error fetching purchase orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await api.get('/vendors?status=active');
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchProcurementItems = async () => {
    try {
      const response = await api.get('/procurement-items');
      setProcurementItems(response.data);
    } catch (error) {
      console.error('Error fetching procurement items:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/purchase-orders/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching PO stats:', error);
    }
  };

  const handleOpenDialog = (mode, po = null) => {
    setDialogMode(mode);
    setSelectedPO(po);
    if (po && mode !== 'create') {
      setFormData({
        poNumber: po.poNumber || '',
        vendor: po.vendor?._id || '',
        project: po.project || '',
        requestedBy: po.requestedBy || '',
        items: po.items || [],
        deliveryAddress: po.deliveryAddress || {
          street: '',
          city: '',
          state: '',
          zipCode: ''
        },
        expectedDeliveryDate: po.expectedDeliveryDate ? new Date(po.expectedDeliveryDate) : null,
        paymentTerms: po.paymentTerms || '',
        notes: po.notes || '',
        urgency: po.urgency || 'normal'
      });
    } else {
      setFormData({
        poNumber: '',
        vendor: '',
        project: '',
        requestedBy: user?.name || '',
        items: [],
        deliveryAddress: {
          street: '',
          city: '',
          state: '',
          zipCode: ''
        },
        expectedDeliveryDate: null,
        paymentTerms: '',
        notes: '',
        urgency: 'normal'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPO(null);
    setNewItem({
      item: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      unit: 'pcs'
    });
  };

  const handleSubmit = async () => {
    try {
      if (dialogMode === 'create') {
        await api.post('/purchase-orders', formData);
        setSuccess('Purchase order created successfully');
      } else if (dialogMode === 'edit') {
        await api.put(`/purchase-orders/${selectedPO._id}`, formData);
        setSuccess('Purchase order updated successfully');
      }
      
      handleCloseDialog();
      fetchPurchaseOrders();
      fetchStats();
    } catch (error) {
      setError('Failed to save purchase order');
      console.error('Error saving purchase order:', error);
    }
  };

  const handleDelete = async (poId) => {
    if (window.confirm('Are you sure you want to delete this purchase order?')) {
      try {
        await api.delete(`/purchase-orders/${poId}`);
        setSuccess('Purchase order deleted successfully');
        fetchPurchaseOrders();
        fetchStats();
      } catch (error) {
        setError('Failed to delete purchase order');
        console.error('Error deleting purchase order:', error);
      }
    }
  };

  const handleApprove = async (poId) => {
    try {
      await api.post(`/purchase-orders/${poId}/approve`);
      setSuccess('Purchase order approved successfully');
      fetchPurchaseOrders();
      fetchStats();
    } catch (error) {
      setError('Failed to approve purchase order');
      console.error('Error approving purchase order:', error);
    }
  };

  const handleReceive = async (poId) => {
    try {
      await api.post(`/purchase-orders/${poId}/receive`);
      setSuccess('Purchase order marked as received');
      fetchPurchaseOrders();
      fetchStats();
    } catch (error) {
      setError('Failed to mark purchase order as received');
      console.error('Error receiving purchase order:', error);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleAddItem = () => {
    if (newItem.item && newItem.quantity > 0) {
      const item = {
        ...newItem,
        totalPrice: newItem.quantity * newItem.unitPrice
      };
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, item]
      }));
      setNewItem({
        item: '',
        description: '',
        quantity: 1,
        unitPrice: 0,
        unit: 'pcs'
      });
    }
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotal = () => {
    return formData.items.reduce((total, item) => total + (item.quantity * item.unitPrice), 0);
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.color : 'default';
  };

  const getStatusLabel = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.label : status;
  };

  const getUrgencyColor = (urgency) => {
    const urgencyOption = urgencyOptions.find(opt => opt.value === urgency);
    return urgencyOption ? urgencyOption.color : 'default';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading purchase orders...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Purchase Orders
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/purchase-orders/create')}
        >
          Create Purchase Order
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssignmentIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Orders
                  </Typography>
                  <Typography variant="h5">
                    {stats.totalOrders}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ScheduleIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Pending Orders
                  </Typography>
                  <Typography variant="h5">
                    {stats.pendingOrders}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MoneyIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Value
                  </Typography>
                  <Typography variant="h5">
                    ${stats.totalValue?.toLocaleString() || '0'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ReceiptIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Avg Order Value
                  </Typography>
                  <Typography variant="h5">
                    ${stats.averageOrderValue?.toLocaleString() || '0'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Search POs"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Vendor</InputLabel>
                <Select
                  value={filters.vendor}
                  label="Vendor"
                  onChange={(e) => setFilters(prev => ({ ...prev, vendor: e.target.value }))}
                >
                  <MenuItem value="">All Vendors</MenuItem>
                  {vendors.map((vendor) => (
                    <MenuItem key={vendor._id} value={vendor._id}>
                      {vendor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="From Date"
                value={filters.dateFrom}
                onChange={(date) => setFilters(prev => ({ ...prev, dateFrom: date }))}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="To Date"
                value={filters.dateTo}
                onChange={(date) => setFilters(prev => ({ ...prev, dateTo: date }))}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setFilters({ status: '', vendor: '', search: '', dateFrom: null, dateTo: null })}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Purchase Orders Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>PO Number</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Urgency</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Expected Delivery</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrders.map((po) => (
                  <TableRow key={po._id}>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {po.poNumber}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {new Date(po.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {po.vendor?.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(po.status)}
                        size="small"
                        color={getStatusColor(po.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={po.urgency}
                        size="small"
                        color={getUrgencyColor(po.urgency)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        ${po.totalAmount?.toLocaleString() || '0'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {po.expectedDeliveryDate ? 
                          new Date(po.expectedDeliveryDate).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog('view', po)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {po.status === 'draft' && (
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('edit', po)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {po.status === 'pending_approval' && (
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApprove(po._id)}
                          >
                            <ApproveIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {['confirmed', 'partially_received'].includes(po.status) && (
                        <Tooltip title="Mark as Received">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleReceive(po._id)}
                          >
                            <ShippingIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {po.status === 'draft' && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(po._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Purchase Order Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create Purchase Order' : 
           dialogMode === 'edit' ? 'Edit Purchase Order' : 'Purchase Order Details'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PO Number"
                value={formData.poNumber}
                onChange={(e) => handleInputChange('poNumber', e.target.value)}
                disabled={dialogMode === 'view'}
                placeholder="Auto-generated if empty"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Vendor</InputLabel>
                <Select
                  value={formData.vendor}
                  label="Vendor"
                  onChange={(e) => handleInputChange('vendor', e.target.value)}
                  disabled={dialogMode === 'view'}
                >
                  {vendors.map((vendor) => (
                    <MenuItem key={vendor._id} value={vendor._id}>
                      {vendor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project"
                value={formData.project}
                onChange={(e) => handleInputChange('project', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Requested By"
                value={formData.requestedBy}
                onChange={(e) => handleInputChange('requestedBy', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Expected Delivery Date"
                value={formData.expectedDeliveryDate}
                onChange={(date) => handleInputChange('expectedDeliveryDate', date)}
                disabled={dialogMode === 'view'}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Urgency</InputLabel>
                <Select
                  value={formData.urgency}
                  label="Urgency"
                  onChange={(e) => handleInputChange('urgency', e.target.value)}
                  disabled={dialogMode === 'view'}
                >
                  {urgencyOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Delivery Address */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Delivery Address
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.deliveryAddress.street}
                onChange={(e) => handleInputChange('deliveryAddress.street', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.deliveryAddress.city}
                onChange={(e) => handleInputChange('deliveryAddress.city', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                value={formData.deliveryAddress.state}
                onChange={(e) => handleInputChange('deliveryAddress.state', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Zip Code"
                value={formData.deliveryAddress.zipCode}
                onChange={(e) => handleInputChange('deliveryAddress.zipCode', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>

            {/* Items Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Items
              </Typography>
            </Grid>
            
            {dialogMode !== 'view' && (
              <>
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    options={procurementItems}
                    getOptionLabel={(option) => option.name || ''}
                    value={procurementItems.find(item => item._id === newItem.item) || null}
                    onChange={(event, newValue) => {
                      setNewItem(prev => ({
                        ...prev,
                        item: newValue?._id || '',
                        description: newValue?.description || '',
                        unitPrice: newValue?.currentPrice || 0
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Item" fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="Quantity"
                    type="number"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth>
                    <InputLabel>Unit</InputLabel>
                    <Select
                      value={newItem.unit}
                      label="Unit"
                      onChange={(e) => setNewItem(prev => ({ ...prev, unit: e.target.value }))}
                    >
                      {units.map((unit) => (
                        <MenuItem key={unit} value={unit}>
                          {unit}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="Unit Price"
                    type="number"
                    value={newItem.unitPrice}
                    onChange={(e) => setNewItem(prev => ({ ...prev, unitPrice: parseFloat(e.target.value) || 0 }))}
                    InputProps={{ startAdornment: '$' }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="Total"
                    value={`$${(newItem.quantity * newItem.unitPrice).toFixed(2)}`}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={1}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleAddItem}
                    sx={{ height: '56px' }}
                  >
                    <AddIcon />
                  </Button>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              {formData.items.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Unit Price</TableCell>
                        <TableCell>Total</TableCell>
                        {dialogMode !== 'view' && <TableCell>Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.item}</TableCell>
                          <TableCell>{item.description}</TableCell>
                          <TableCell>{item.quantity} {item.unit}</TableCell>
                          <TableCell>${item.unitPrice}</TableCell>
                          <TableCell>${(item.quantity * item.unitPrice).toFixed(2)}</TableCell>
                          {dialogMode !== 'view' && (
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveItem(index)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={dialogMode !== 'view' ? 4 : 3} align="right">
                          <Typography variant="h6">Total:</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="h6">
                            ${calculateTotal().toFixed(2)}
                          </Typography>
                        </TableCell>
                        {dialogMode !== 'view' && <TableCell />}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary" align="center" sx={{ py: 2 }}>
                  No items added yet
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Payment Terms"
                value={formData.paymentTerms}
                onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                disabled={dialogMode === 'view'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {dialogMode !== 'view' && (
            <Button onClick={handleSubmit} variant="contained">
              {dialogMode === 'create' ? 'Create' : 'Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PurchaseOrders;